import { Box } from '@mui/material';
import { MapboxMapContext } from '@sofarocean/mapbox-context';
import { isNil } from 'lodash';
import mapboxgl, { LngLatLike, MarkerOptions } from 'mapbox-gl';
import { ReactNode, useContext, useEffect, useRef } from 'react';

import { SpotterLocation } from '@/common/types';
import { lngLat } from '@/utils';

export type MapMarkerProps = Omit<MarkerOptions, 'element'> & {
    children?: ReactNode;
    position?: LngLatLike | SpotterLocation;
};

export const MapMarker = ({ children, position, ...options }: MapMarkerProps) => {
    const { map } = useContext(MapboxMapContext);

    const markerContent = (
        <Box
            sx={{
                overflow: 'visible',
                pointerEvents: 'auto',
                width: 'auto',
                height: 'auto',
            }}
        >
            {children}
        </Box>
    );

    const markerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!position || isNil(markerRef.current) || isNil(map)) {
            return;
        }

        const marker = new mapboxgl.Marker({ ...options, element: markerRef.current })
            .setLngLat(lngLat(position))
            .addTo(map);

        return () => {
            marker.remove();
        };
    }, [map, position, options]);

    return (
        <div style={{ display: 'none' }}>
            {/* This ref element is necessary so React can render the content
          somewhere and then we can direct the mapbox popup to this content
          to render into itself.
          Without this we'd first need to render the content into an HTML
          string, which wasn't obvious without ReactDOMServer.
      */}
            <div ref={markerRef}>{markerContent}</div>
        </div>
    );
};
